import React, { useState, useEffect, useMemo } from 'react';
import { DollarSign, Plus, X, Edit, Trash2, Check, Filter, ChevronDown, Upload, FileText, ExternalLink } from 'lucide-react';
import api, { resolveFileUrl } from '../utils/api';
import { toast } from 'sonner';

const AgencyFees = () => {
  const [fees, setFees] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [filterYear, setFilterYear] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    player_id: '', club_name: '', transfer_type: 'summer_transfer',
    year_period: '1st_year', amount: '', status: 'due',
    year: currentYear, invoice_number: '', invoice_pdf_url: '', invoice_date: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [feesRes, playersRes] = await Promise.all([
        api.get('/agency-fees'),
        api.get('/players')
      ]);
      setFees(feesRes.data);
      setPlayers(playersRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!form.player_id || !form.club_name || !form.amount) {
      toast.error('Please fill in player, club and amount'); return;
    }
    try {
      const payload = { ...form, amount: parseFloat(form.amount), year: parseInt(form.year) };
      if (editingFee) {
        const res = await api.put(`/agency-fees/${editingFee.id}`, payload);
        setFees(fees.map(f => f.id === editingFee.id ? res.data : f));
        toast.success('Invoice updated');
      } else {
        const res = await api.post('/agency-fees', payload);
        setFees([res.data, ...fees]);
        toast.success('Invoice added');
      }
      resetForm();
    } catch { toast.error('Failed to save invoice'); }
  };

  const deleteFee = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await api.delete(`/agency-fees/${id}`);
      setFees(fees.filter(f => f.id !== id));
      toast.success('Invoice deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const resetForm = () => {
    setForm({ player_id: '', club_name: '', transfer_type: 'summer_transfer', year_period: '1st_year', amount: '', status: 'due', year: currentYear, invoice_number: '', invoice_pdf_url: '', invoice_date: '' });
    setEditingFee(null);
    setShowForm(false);
  };

  const startEdit = (fee) => {
    setForm({
      player_id: fee.player_id, club_name: fee.club_name, transfer_type: fee.transfer_type,
      year_period: fee.year_period, amount: fee.amount.toString(), status: fee.status,
      year: fee.year, invoice_number: fee.invoice_number || '', invoice_pdf_url: fee.invoice_pdf_url || '', invoice_date: fee.invoice_date || ''
    });
    setEditingFee(fee);
    setShowForm(true);
  };

  const years = useMemo(() => {
    const yrs = new Set(fees.map(f => f.year));
    [currentYear - 1, currentYear, currentYear + 1].forEach(y => yrs.add(y));
    return [...yrs].sort();
  }, [fees, currentYear]);

  const filteredFees = useMemo(() => {
    return fees.filter(f => {
      if (filterYear !== 'all' && f.year !== parseInt(filterYear)) return false;
      if (filterStatus !== 'all' && f.status !== filterStatus) return false;
      return true;
    });
  }, [fees, filterYear, filterStatus]);

  const summary = useMemo(() => {
    const byYear = {};
    fees.forEach(f => {
      if (!byYear[f.year]) byYear[f.year] = { due: 0, paid: 0 };
      if (f.status === 'paid') byYear[f.year].paid += f.amount;
      else byYear[f.year].due += f.amount;
    });
    const totalDue = fees.filter(f => f.status === 'due').reduce((s, f) => s + f.amount, 0);
    const totalPaid = fees.filter(f => f.status === 'paid').reduce((s, f) => s + f.amount, 0);
    return { byYear, totalDue, totalPaid };
  }, [fees]);

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Please upload a PDF file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/document', formData);
      setForm({ ...form, invoice_pdf_url: res.data.url });
      toast.success('Invoice PDF uploaded');
    } catch { toast.error('Failed to upload PDF'); }
    finally { setUploading(false); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);

  if (loading) return <div className="p-8 text-white">Loading...</div>;

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white" data-testid="agency-fees-title">Agency Fees</h1>
          <p className="text-sm text-zinc-500 mt-1">Invoice tracking & management</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-invoice-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide text-sm rounded-sm transition-all">
          <Plus className="w-4 h-4" /> Add Invoice
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass-card p-5" data-testid="total-due-card">
          <p className="text-xs font-bold uppercase tracking-widest text-red-400 mb-2">Total Due</p>
          <p className="text-2xl font-bold text-white">{fmt(summary.totalDue)}</p>
        </div>
        <div className="glass-card p-5" data-testid="total-paid-card">
          <p className="text-xs font-bold uppercase tracking-widest text-green-400 mb-2">Total Paid</p>
          <p className="text-2xl font-bold text-white">{fmt(summary.totalPaid)}</p>
        </div>
        {Object.entries(summary.byYear).sort(([a],[b]) => a - b).map(([year, data]) => (
          <div key={year} className="glass-card p-5" data-testid={`year-summary-${year}`}>
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">{year}</p>
            <div className="space-y-1">
              <p className="text-sm text-red-400">Due: <span className="font-bold text-white">{fmt(data.due)}</span></p>
              <p className="text-sm text-green-400">Paid: <span className="font-bold text-white">{fmt(data.paid)}</span></p>
            </div>
          </div>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <div className="glass-card p-6 mb-8 border border-[#FFFFFF]/20" data-testid="invoice-form">
          <h3 className="text-lg font-bold uppercase text-white mb-4">{editingFee ? 'Edit Invoice' : 'New Invoice'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Player</label>
              <select value={form.player_id} onChange={(e) => setForm({ ...form, player_id: e.target.value })} data-testid="fee-player-select"
                className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm outline-none">
                <option value="">Select player...</option>
                {players.sort((a,b) => a.name.localeCompare(b.name)).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Club</label>
              <input value={form.club_name} onChange={(e) => setForm({ ...form, club_name: e.target.value })} data-testid="fee-club-input"
                placeholder="Club name" className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Transfer Type</label>
              <select value={form.transfer_type} onChange={(e) => setForm({ ...form, transfer_type: e.target.value })} data-testid="fee-transfer-type"
                className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm outline-none">
                <option value="summer_transfer">Summer Transfer</option>
                <option value="winter_transfer">Winter Transfer</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Year Period</label>
              <select value={form.year_period} onChange={(e) => setForm({ ...form, year_period: e.target.value })} data-testid="fee-year-period"
                className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm outline-none">
                <option value="1st_year">1st Year</option>
                <option value="2nd_year">2nd Year</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Amount (GBP)</label>
              <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="fee-amount-input"
                placeholder="0.00" className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="fee-status-select"
                className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm outline-none">
                <option value="due">Due</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Year</label>
              <select value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="fee-year-select"
                className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm outline-none">
                {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Invoice Date</label>
              <input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="fee-date-input"
                className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Invoice Number</label>
              <input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="fee-invoice-number-input"
                placeholder="e.g. INV-2026-001" className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Invoice PDF</label>
              <div className="flex items-center gap-2">
                <label className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm cursor-pointer transition-all ${uploading ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-900 border border-white/10 text-zinc-300 hover:border-[#FFFFFF]/50'}`}>
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : form.invoice_pdf_url ? 'Replace PDF' : 'Upload PDF'}
                  <input type="file" accept=".pdf" onChange={handlePdfUpload} className="hidden" disabled={uploading} data-testid="fee-pdf-upload" />
                </label>
                {form.invoice_pdf_url && (
                  <a href={resolveFileUrl(form.invoice_pdf_url)} target="_blank" rel="noopener noreferrer" className="text-white text-xs flex items-center gap-1 hover:underline">
                    <FileText className="w-3 h-3" /> View PDF
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={handleSubmit} data-testid="save-invoice-btn"
              className="px-5 py-2.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-sm rounded-sm transition-all flex items-center gap-2">
              <Check className="w-4 h-4" /> {editingFee ? 'Update' : 'Save'}
            </button>
            <button onClick={resetForm} className="px-5 py-2.5 bg-white/10 text-white hover:bg-white/20 text-sm rounded-sm transition-all flex items-center gap-2">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-500" />
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} data-testid="filter-year"
            className="bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm outline-none">
            <option value="all">All Years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status"
          className="bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm outline-none">
          <option value="all">All Statuses</option>
          <option value="due">Due</option>
          <option value="paid">Paid</option>
        </select>
        <span className="text-zinc-500 text-sm self-center">{filteredFees.length} invoice{filteredFees.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Invoice Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="invoice-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Player</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Club</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Transfer</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Period</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Year</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Amount</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Invoice #</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">PDF</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFees.length > 0 ? filteredFees.map((fee) => (
                <tr key={fee.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`invoice-row-${fee.id}`}>
                  <td className="px-4 py-3 text-sm text-white font-medium">{fee.player_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-zinc-300">{fee.club_name}</td>
                  <td className="px-4 py-3 text-sm text-zinc-300">{fee.transfer_type === 'summer_transfer' ? 'Summer' : 'Winter'}</td>
                  <td className="px-4 py-3 text-sm text-zinc-300">{fee.year_period === '1st_year' ? '1st Year' : '2nd Year'}</td>
                  <td className="px-4 py-3 text-sm text-zinc-300">{fee.year}</td>
                  <td className="px-4 py-3 text-sm text-white font-bold text-right">{fmt(fee.amount)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase ${
                      fee.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>{fee.status}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-300">{fee.invoice_number || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {fee.invoice_pdf_url ? (
                      <a href={resolveFileUrl(fee.invoice_pdf_url)} target="_blank" rel="noopener noreferrer" data-testid={`view-pdf-${fee.id}`}
                        className="inline-flex items-center gap-1 text-white hover:underline text-xs">
                        <FileText className="w-3.5 h-3.5" /> View
                      </a>
                    ) : <span className="text-zinc-600 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => startEdit(fee)} data-testid={`edit-fee-${fee.id}`}
                        className="p-1.5 text-zinc-400 hover:text-blue-400 transition-colors"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => deleteFee(fee.id)} data-testid={`delete-fee-${fee.id}`}
                        className="p-1.5 text-zinc-400 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-zinc-500">No invoices found. Click "Add Invoice" to create one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgencyFees;
